import React from "react";

import { isTruthy } from "~/tools/is-truthy";
import type { Record } from "~/tools/record";

import type { MonitorCheckStatus } from "../../../../../../common/openapi";
import { RadioButton, RadioButtonGroup } from "../../../../../../components/react/buttons/RadioButton";
import { RemediationGuide } from "../../../../highlights-perspective/monitor-status-drawer/RemediationGuide";

import { MonitorStatusMetrics } from "./MonitorStatusMetrics";
import { MonitorStatusRelatedViolations } from "./MonitorStatusRelatedViolations";

type UnhealthyDetailsTab =
	| { type: "REMEDIATION_GUIDE"; troubleshootingSteps: string }
	| { type: "RELATED_VIOLATIONS" }
	| { type: "METRICS" };

const getAvailableUnhealthyDetailsTabs = (monitorStatus: MonitorCheckStatus): ReadonlyArray<UnhealthyDetailsTab> => {
	const tabs: ReadonlyArray<false | UnhealthyDetailsTab> = [
		monitorStatus.troubleshootingSteps != null && {
			type: "REMEDIATION_GUIDE",
			troubleshootingSteps: monitorStatus.troubleshootingSteps,
		},

		{ type: "RELATED_VIOLATIONS" },

		monitorStatus.metrics.length > 0 && { type: "METRICS" },
	];

	return tabs.filter(isTruthy);
};

const DETAILS_TYPE_TO_LABEL = {
	REMEDIATION_GUIDE: "Remediation guide",
	RELATED_VIOLATIONS: "Related violations",
	METRICS: "Metrics",
} satisfies Record<UnhealthyDetailsTab["type"]>;

export interface MonitorStatusUnhealthyDetailsProps {
	monitorStatus: MonitorCheckStatus;
}

export const MonitorStatusUnhealthyDetails: React.FC<MonitorStatusUnhealthyDetailsProps> = ({ monitorStatus }) => {
	const tabs = getAvailableUnhealthyDetailsTabs(monitorStatus);
	const [currentTab, setCurrentTab] = React.useState(tabs[0].type);

	// fallback to the first tab if the current tab is not available anymore for any reasons
	const tab = tabs.find((tab) => tab.type === currentTab) ?? tabs[0];

	return (
		<div className="space-y-4" data-testid="monitor-status-unhealthy-details">
			<RadioButtonGroup className="flex" onChange={setCurrentTab} size="small" value={tab.type}>
				{tabs.map(({ type }) => (
					<RadioButton
						className="h-[26px]! flex-auto text-center text-gray-800 text-xs leading-[23px]!"
						key={type}
						value={type}
					>
						{DETAILS_TYPE_TO_LABEL[type]}
					</RadioButton>
				))}
			</RadioButtonGroup>

			{tab.type === "REMEDIATION_GUIDE" && (
				<RemediationGuide
					componentIdentifier={monitorStatus.component.identifier}
					showPinnedPane
					text={tab.troubleshootingSteps}
					timestamp={monitorStatus.topologyTime}
				/>
			)}

			{tab.type === "RELATED_VIOLATIONS" && <MonitorStatusRelatedViolations monitorStatus={monitorStatus} />}

			{tab.type === "METRICS" && <MonitorStatusMetrics metrics={monitorStatus.metrics} />}
		</div>
	);
};
